import React, { useState, useMemo, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import AddExperienceModal from '../Experience/AddExperienceModal';
import { useUserContext } from '../../contexts/UserContext';
import { getTealiumExperiences } from '../../utils/APIs';
import { toast } from 'react-toastify';
import { useContentfulContext } from '../../contexts/ContentfulContext';
import { Card, Button, Input, AgTableCard } from '../UI';

const ManageTealiumExperiences = () => {
    const [rowData, setRowData] = useState([]);
    const [showAddExperiencesModal, setShowAddExperiencesModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [gridApi, setGridApi] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    const { userData } = useUserContext();
    const { enums, content } = useContentfulContext();
    const navigator = useNavigate();

    useEffect(() => {
        if (userData?.defaultTealiumKey) {
            getAllTealiumExperience();
        }
    }, [userData?.defaultTealiumKey]);

    const getAllTealiumExperience = async () => {
        try {
            if (!userData?.defaultTealiumKey) {
                toast.error(content.LIST_TEALIUM_EXPERIENCES_CONTENT.error.no_tealium_found);
                return;
            }
            setLoading(true);
            const result = await getTealiumExperiences({
                token: userData?.token,
                organizationId: userData?.selectedOrganization?.org_token,
                tealium_id: userData?.defaultTealiumKey?._id,
            });

            setRowData(result.data?.extensions || []);
        } catch (error) {
            toast.error(error?.message || content?.COMMON_MESSAGE.something_wrong_msg);
        } finally {
            setLoading(false);
        }
    };

    const onGridReady = (params) => {
        setGridApi(params.api);
    };

    const autoSizeStrategy = useMemo(() => {
        return {
            type: "fitGridWidth",
            defaultMinWidth: 40,
            columnLimits: [
                {
                    colId: "id",
                    minWidth: 80,
                },
                {
                    colId: "extensionId",
                    minWidth: 140,
                },
                {
                    colId: "name",
                    minWidth: 220,
                },
                {
                    colId: "status",
                    minWidth: 120,
                },
                {
                    colId: "extensionType",
                    minWidth: 140,
                },
                {
                    colId: "scope",
                    minWidth: 120,
                },
                {
                    colId: "occurrence",
                    minWidth: 140,
                },
                {
                    colId: "selectedTargets",
                    minWidth: 180,
                },
            ],
        };
    }, []);

    const handleCloseModal = () => {
        setShowAddExperiencesModal(false);
    };

    const onFilterTextBoxChanged = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        if (gridApi) {
            gridApi.setFilterModel({
                ...gridApi.getFilterModel(),
                name: value ? { type: "contains", filter: value.toLowerCase() } : null,
            });
        }
    };

    const handleClearSearch = () => {
        setSearchTerm('');
        if (gridApi) {
            const currentFilter = gridApi.getFilterModel();
            delete currentFilter.name;
            gridApi.setFilterModel(currentFilter);
        }
    };

    const colDefs = [
        {
            headerName: "ID",
            field: "id",
            cellRenderer: ({ data }) => (
                <Link
                    className="font-medium text-primary hover:text-primary-hover hover:underline"
                    to={`/experience/tealium-experiences/${data.id}`}
                >
                    {data.id}
                </Link>
            ),
            suppressMovable: true,
            sortable: true,
            searchable: true,
            resizable: true,
            filter: true,
        },
        {
            headerName: "Extension ID",
            field: "extensionId",
            suppressMovable: true,
            sortable: true,
            filter: true,
            cellRenderer: ({ value }) => (
                <span className="font-mono text-xs text-stride-muted">{value || '—'}</span>
            )
        },
        {
            headerName: "Name",
            field: "name",
            suppressMovable: true,
            sortable: true,
            searchable: true,
            resizable: true,
            filter: true,
            cellRenderer: ({ value }) => (
                <span className="font-medium text-stride-heading">{value || '—'}</span>
            )
        },
        {
            headerName: "Status",
            field: "status",
            suppressMovable: true,
            sortable: true,
            filter: true,
            cellRenderer: ({ value }) => {
                const isActive = value?.toLowerCase() === 'active';
                return (
                    <span className={`stride-badge-pill ${isActive ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                        {value || 'inactive'}
                    </span>
                );
            }
        },
        {
            headerName: "Extension Type",
            field: "extensionType",
            suppressMovable: true,
            sortable: true,
            filter: true,
            cellRenderer: ({ value }) => (
                <span className="text-stride-body text-xs">{value || '—'}</span>
            )
        },
        {
            headerName: "Scope",
            field: "scope",
            suppressMovable: true,
            sortable: true,
            filter: true,
            cellRenderer: ({ value }) => (
                <span className="stride-badge-pill bg-primary-soft text-primary font-medium text-[11px]">
                    {value || '—'}
                </span>
            )
        },
        {
            headerName: "Occurrence",
            field: "occurrence",
            suppressMovable: true,
            sortable: true,
            filter: true,
        },
        {
            headerName: "Selected Targets",
            field: "selectedTargets",
            cellRenderer: ({ data }) => {
                if (!data?.selectedTargets) return <span className="text-stride-muted">—</span>;
                const targets = Object.keys(data.selectedTargets)
                    .filter(key => data.selectedTargets[key])
                    .join(', ');
                return <span className="truncate" title={targets}>{targets || '—'}</span>;
            },
            filter: true,
            suppressMovable: true
        },
    ];

    return (
        <>
            <main id="main" className="stride-page-shell flex flex-col space-y-6">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <span className="stride-eyebrow">Experiences / Manage</span>
                        <h1 className="text-2xl font-bold tracking-tight text-stride-heading">
                            {content.LIST_TEALIUM_EXPERIENCES_CONTENT.title}
                        </h1>
                        <p className="text-sm text-stride-muted mt-1">
                            {rowData?.length || 0} total Tealium experiences synced
                        </p>
                    </div>
                </div>

                {/* Empty State */}
                {!userData?.defaultTealiumKey && (
                    <Card className="text-center p-12 max-w-lg mx-auto">
                        <p className="text-base text-stride-heading font-medium mb-4">
                            {content.LIST_TEALIUM_EXPERIENCES_CONTENT.no_tealium_key}
                        </p>
                        <Button
                            variant="primary"
                            onClick={() => navigator('/setup/tealium-new-integration')}
                        >
                            {content.LIST_TEALIUM_EXPERIENCES_CONTENT.create_new_tealium_io}
                        </Button>
                    </Card>
                )}

                <AddExperienceModal
                    showAddExperiencesModal={showAddExperiencesModal}
                    handleCloseModal={handleCloseModal}
                />

                {/* Quartz AG-Grid Table inside Card with Search in Header */}
                {userData?.defaultTealiumKey?._id && (
                    <AgTableCard
                        header={
                            <div className="w-full md:w-80">
                                <Input
                                    placeholder="Search by name..."
                                    value={searchTerm}
                                    onChange={onFilterTextBoxChanged}
                                    prefixIcon={<Search className="w-4 h-4 text-stride-muted" />}
                                    clearable
                                    onClear={handleClearSearch}
                                />
                            </div>
                        }
                        tableId="manage-tealium-experiences-table"
                        loading={loading}
                        rowData={rowData}
                        columnDefs={colDefs}
                        autoSizeStrategy={autoSizeStrategy}
                        onGridReady={onGridReady}
                        rowSelection="multiple"
                        suppressRowClickSelection
                        pagination={true}
                        paginationPageSize={content.LIST_TEALIUM_EXPERIENCES_CONTENT.default_page_size}
                        paginationPageSizeSelector={enums?.PAGINATION_PAGE_SIZE_SELECTOR}
                    />
                )}
            </main>
        </>
    );
};

export default ManageTealiumExperiences;
